import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({});
  const searchTimeout = useRef(null);

  const [reminderPreview, setReminderPreview] = useState(null); // { html, count, recipients }
  const [reminderSelected, setReminderSelected] = useState({}); // ticket_id -> bool
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderResult, setReminderResult] = useState('');

  const load = (eid, q) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (eid) params.set('event_id', eid);
    if (q) params.set('q', q);
    const qs = params.toString();
    api.get(qs ? `/api/admin/tickets?${qs}` : '/api/admin/tickets')
      .then((r) => setTickets(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); load('', ''); }, []);

  const onSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => load(filterEvent, q), 300);
  };

  const action = async (id, endpoint) => {
    try { await api.post(`/api/admin/tickets/${id}/${endpoint}`, { admin_comment: comments[id] || '' }); load(filterEvent, search); }
    catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteTicket = async (id, code) => {
    if (!window.confirm(`Ticket #${code} endgültig löschen?`)) return;
    try { await api.delete(`/api/admin/tickets/${id}`); load(filterEvent, search); }
    catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const openReminderPreview = async () => {
    if (!filterEvent) { alert('Bitte zuerst ein Event auswählen.'); return; }
    setReminderLoading(true);
    setReminderResult('');
    try {
      const r = await api.get(`/api/admin/tickets/payment-reminder/preview?event_id=${filterEvent}`);
      setReminderPreview(r.data);
      const selected = {};
      r.data.recipients.forEach((rec) => { selected[rec.ticket_id] = true; });
      setReminderSelected(selected);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setReminderLoading(false); }
  };

  const toggleRecipient = (ticketId) => {
    setReminderSelected((prev) => ({ ...prev, [ticketId]: !prev[ticketId] }));
  };

  const selectedTicketIds = Object.entries(reminderSelected).filter(([, v]) => v).map(([k]) => parseInt(k, 10));

  const sendReminders = async () => {
    if (!filterEvent || selectedTicketIds.length === 0) return;
    if (!window.confirm(`Erinnerungsmail an ${selectedTicketIds.length} Käufer mit ausstehender Zahlung senden?`)) return;
    setReminderSending(true);
    try {
      const r = await api.post('/api/admin/tickets/payment-reminder/send', {
        event_id: parseInt(filterEvent, 10),
        ticket_ids: selectedTicketIds,
      });
      setReminderResult(`✅ Erinnerung an ${r.data.sent} Empfänger gesendet.`);
      setReminderPreview(null);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setReminderSending(false); }
  };

  const STATUS_FILTERS = [
    { key: 'all',      label: 'Alle' },
    { key: 'pending',  label: 'Ausstehend' },
    { key: 'unpaid',   label: 'Nicht bezahlt' },
    { key: 'paid',     label: 'Bezahlt' },
  ];

  const visibleTickets = tickets.filter((t) => {
    if (filterStatus === 'pending')  return t.status === 'pending_payment';
    if (filterStatus === 'unpaid')   return t.payment_status === 'open';
    if (filterStatus === 'paid')     return t.payment_status === 'confirmed';
    return true;
  });

  return (
    <div>
      <h2 className="page-title">Tickets</h2>
      <div className="mb-4 flex flex-wrap gap-2">
        <select className="input w-auto min-w-48" value={filterEvent} onChange={(e) => { setFilterEvent(e.target.value); load(e.target.value, search); }}>
          <option value="">Alle Events</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input
          value={search}
          onChange={onSearchChange}
          placeholder="Name, Nickname oder Ticket-Code…"
          className="input flex-1 min-w-48"
        />
        <Button variant="secondary" size="sm" onClick={openReminderPreview} disabled={reminderLoading}>
          {reminderLoading ? 'Lade…' : '⏰ Zahlungserinnerung'}
        </Button>
      </div>

      {reminderResult && <p className="text-sm text-emerald-700 mb-4">{reminderResult}</p>}

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {STATUS_FILTERS.map(({ key, label }) => {
          const count = key === 'all' ? tickets.length
            : key === 'pending' ? tickets.filter((t) => t.status === 'pending_payment').length
            : key === 'unpaid'  ? tickets.filter((t) => t.payment_status === 'open').length
            : tickets.filter((t) => t.payment_status === 'confirmed').length;
          return (
            <button key={key} onClick={() => setFilterStatus(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filterStatus === key
                  ? 'bg-ocean-600 text-white border-ocean-600'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-ocean-300 hover:text-ocean-600'
              }`}>
              {label} <span className={`ml-1 ${filterStatus === key ? 'opacity-70' : 'opacity-50'}`}>({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-7 w-7 border-2 border-ocean-400 border-t-transparent"/></div> : (
        <div className="space-y-3">
          {visibleTickets.map((t) => (
            <div key={t.id} className="card">
              <div className="flex flex-wrap justify-between gap-2 mb-3">
                <div>
                  <p className="font-mono font-bold text-ocean-800">#{t.ticket_code}</p>
                  <p className="text-xs text-slate-500">{t.buyer?.firstname} {t.buyer?.lastname} ({t.buyer?.username}) · Event #{t.event_id}</p>
                  {t.seat_label && <p className="text-xs text-reef-600">💺 {t.seat_label}</p>}
                  {t.payment_reference && <p className="text-xs text-sand-700 font-medium">📎 Ref: {t.payment_reference}</p>}
                </div>
                <div className="flex gap-1.5 flex-wrap items-start">
                  <Badge status={t.status} />
                  <Badge status={t.payment_status} />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <input className="input text-xs flex-1 min-w-40" placeholder="Admin-Kommentar"
                  value={comments[t.id] || ''} onChange={(e) => setComments({ ...comments, [t.id]: e.target.value })} />
                {t.payment_status === 'open' && t.status === 'pending_payment' && (
                  <>
                    <Button variant="success" size="sm" onClick={() => action(t.id, 'confirm-payment')}>✓ Bestätigen</Button>
                    <Button variant="danger" size="sm" onClick={() => action(t.id, 'reject-payment')}>✗ Ablehnen</Button>
                  </>
                )}
                {t.status !== 'cancelled' && <Button variant="ghost" size="sm" onClick={() => action(t.id, 'cancel')}>Stornieren</Button>}
                {t.status === 'cancelled' && <Button variant="danger" size="sm" onClick={() => deleteTicket(t.id, t.ticket_code)}>Löschen</Button>}
              </div>
            </div>
          ))}
          {visibleTickets.length === 0 && <div className="card text-center py-8 text-slate-400">Keine Tickets gefunden.</div>}
        </div>
      )}

      {reminderPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setReminderPreview(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 pb-3 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-lg text-ocean-800">Vorschau Zahlungserinnerung</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {reminderPreview.count} Käufer mit ausstehender Zahlung gefunden.
                </p>
              </div>
              <button onClick={() => setReminderPreview(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {reminderPreview.recipients.length > 0 && (
                <div className="p-4 border-b border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Empfänger</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {reminderPreview.recipients.map((rec) => (
                      <label key={rec.ticket_id} className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!reminderSelected[rec.ticket_id]}
                          onChange={() => toggleRecipient(rec.ticket_id)}
                        />
                        <span className="font-medium text-slate-700">{rec.name}</span>
                        <span className="text-slate-400">({rec.email})</span>
                        <span className="text-slate-400 font-mono text-xs ml-auto">#{rec.ticket_code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-2 bg-slate-100">
                <iframe
                  title="E-Mail-Vorschau"
                  srcDoc={reminderPreview.html}
                  className="w-full bg-white rounded-lg border border-slate-200"
                  style={{ height: '50vh' }}
                />
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setReminderPreview(null)}>Abbrechen</Button>
              <Button
                variant="primary"
                disabled={selectedTicketIds.length === 0 || reminderSending}
                onClick={sendReminders}
              >
                {reminderSending ? 'Sende…' : `An ${selectedTicketIds.length} Käufer senden`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
